import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Plus, X, Bike, DollarSign, AlertTriangle, MapPin, SortAsc, SortDesc, Trash2 } from 'lucide-react';
import { getDB, isFirebaseConfigured } from '../lib/firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  addDoc,
  updateDoc, 
  deleteDoc,
  query, 
  where,
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { COLLECTIONS, Bike as BikeType, BikeStatus, ServiceCentre } from '../lib/database.types';
import { EditBikeModal } from './modals/EditBikeModal';

export function BikeInventory() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bikes, setBikes] = useState<BikeType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditBikeModal, setShowEditBikeModal] = useState<string | null>(null);
  const [sortField, setSortField] = useState<keyof BikeType>('model');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filterStatus, setFilterStatus] = useState<BikeStatus | 'all'>('all');
  
  // Form state for new bike
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    rego_number: '',
    rego_expiry: '',
    rate_a: 0,
    rate_b: 0,
    daily_rate: 0,
    service_centre: 'five_dock' as ServiceCentre,
    notes: ''
  });

  useEffect(() => {
    fetchBikes();
  }, []);

  const fetchBikes = async () => {
    try {
      setLoading(true);
      
      if (!isFirebaseConfigured()) {
        // Mock data for development
        setBikes([
          {
            id: '1',
            make: 'Honda',
            model: 'CBR300R',
            rego_number: 'ABC123',
            rego_expiry: '2024-12-31',
            rate_a: 45,
            rate_b: 35,
            daily_rate: 40,
            status: 'available',
            service_centre: 'five_dock',
            created_at: new Date()
          }
        ]);
        return;
      }

      const bikesSnapshot = await getDocs(collection(getDB(), COLLECTIONS.BIKES));
      const bikesData = bikesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as BikeType[];

      setBikes(bikesData);
    } catch (error) {
      console.error('Error fetching bikes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (!isFirebaseConfigured()) {
        console.log('Mock: Would create bike:', formData);
        // Add to local state for demo
        const newBike: BikeType = {
          id: Date.now().toString(),
          ...formData,
          status: 'available',
          created_at: new Date()
        };
        setBikes(prev => [...prev, newBike]);
        setShowAddForm(false);
        resetForm();
        return;
      }

      await addDoc(collection(getDB(), COLLECTIONS.BIKES), {
        ...formData,
        status: 'available',
        created_at: serverTimestamp(),
        updated_at: serverTimestamp()
      });

      await fetchBikes();
      setShowAddForm(false);
      resetForm();
    } catch (error) {
      console.error('Error creating bike:', error);
      alert('Error creating bike');
    }
  };

  const resetForm = () => {
    setFormData({
      make: '',
      model: '',
      rego_number: '',
      rego_expiry: '',
      rate_a: 0,
      rate_b: 0,
      daily_rate: 0,
      service_centre: 'five_dock',
      notes: ''
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name.includes('rate') || name.includes('daily_rate') ? Number(value) : value
    }));
  };

  const handleSort = (field: keyof BikeType) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const deleteBike = async (bikeId: string) => {
    if (!confirm('Are you sure you want to delete this bike?')) return;
    
    try {
      if (!isFirebaseConfigured()) {
        setBikes(prev => prev.filter(bike => bike.id !== bikeId));
        return;
      }

      await deleteDoc(doc(getDB(), COLLECTIONS.BIKES, bikeId));
      await fetchBikes();
    } catch (error) {
      console.error('Error deleting bike:', error);
      alert('Error deleting bike');
    }
  };

  // Filter and sort bikes
  const filteredBikes = bikes.filter(bike => 
    filterStatus === 'all' || bike.status === filterStatus
  );

  const sortedBikes = [...filteredBikes].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    
    if (aValue === undefined) return 1;
    if (bValue === undefined) return -1;
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Check for expiring registrations
  const expiringRegos = bikes.filter(bike => {
    if (!bike.rego_expiry) return false;
    const expiryDate = new Date(bike.rego_expiry);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return expiryDate <= thirtyDaysFromNow;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Bike Inventory</h1>
        {user?.hasFullAccess && (
          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Bike
          </button>
        )}
      </div>

      {/* Expiring Registrations Alert */}
      {expiringRegos.length > 0 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Registrations Expiring Soon ({expiringRegos.length})
              </h3>
              <div className="mt-2 space-y-1">
                {expiringRegos.map(bike => (
                  <p key={bike.id} className="text-sm text-yellow-700">
                    {bike.make} {bike.model} ({bike.rego_number}) - Expires {bike.rego_expiry ? new Date(bike.rego_expiry).toLocaleDateString() : 'Not set'}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex space-x-4">
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as BikeStatus | 'all')}
          className="px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="all">All Status</option>
          <option value="available">Available</option>
          <option value="delivered">Delivered</option>
          <option value="maintenance">Maintenance</option>
          <option value="decommissioned">Decommissioned</option>
        </select>
      </div>

      {/* Bikes Table */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('make')}
              >
                <div className="flex items-center">
                  Make/Model
                  {sortField === 'make' && (
                    sortDirection === 'asc' ? <SortAsc className="w-4 h-4 ml-1" /> : <SortDesc className="w-4 h-4 ml-1" />
                  )}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Registration
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rates
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Service Centre
              </th>
              {user?.hasFullAccess && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedBikes.map((bike) => (
              <tr 
                key={bike.id} 
                onClick={() => bike.id && setShowEditBikeModal(bike.id)}
                className="hover:bg-gray-50 cursor-pointer"
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <Bike className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {bike.make} {bike.model}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{bike.rego_number}</div>
                  <div className="text-sm text-gray-500">
                    Expires: {bike.rego_expiry ? new Date(bike.rego_expiry).toLocaleDateString() : 'Not set'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    <div>Rate A: ${bike.rate_a || 0}/day</div>
                    <div>Rate B: ${bike.rate_b || 0}/day</div>
                    <div>Daily: ${bike.daily_rate || 0}/day</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    bike.status === 'available' ? 'bg-green-100 text-green-800' :
                    bike.status === 'delivered' ? 'bg-blue-100 text-blue-800' :
                    bike.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {bike.status || 'unknown'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 text-gray-400 mr-1" />
                    {bike.service_centre || 'Not set'}
                  </div>
                </td>
                {user?.hasFullAccess && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        bike.id && deleteBike(bike.id);
                      }}
                      className="text-red-600 hover:text-red-900 ml-4"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Bike Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Add New Bike</h2>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Make
                  </label>
                  <input
                    type="text"
                    name="make"
                    value={formData.make}
                    onChange={handleChange}
                    required
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Model
                  </label>
                  <input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    required
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Registration Number
                  </label>
                  <input
                    type="text"
                    name="rego_number"
                    value={formData.rego_number}
                    onChange={handleChange}
                    required
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Registration Expiry
                  </label>
                  <input
                    type="date"
                    name="rego_expiry"
                    value={formData.rego_expiry}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rate A ($)
                  </label>
                  <input
                    type="number"
                    name="rate_a"
                    value={formData.rate_a}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rate B ($)
                  </label>
                  <input
                    type="number"
                    name="rate_b"
                    value={formData.rate_b}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Daily Rate ($)
                  </label>
                  <input
                    type="number"
                    name="daily_rate"
                    value={formData.daily_rate}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Centre
                </label>
                <select
                  name="service_centre"
                  value={formData.service_centre}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="five_dock">Five Dock</option>
                  <option value="seven_hills">Seven Hills</option>
                  <option value="reza">Reza</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add Bike
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Bike Modal */}
      {showEditBikeModal && (
        <EditBikeModal
          bikeId={showEditBikeModal}
          onClose={() => setShowEditBikeModal(null)}
          onBikeUpdated={fetchBikes}
        />
      )}
    </div>
  );
} 